/* eslint-disable react/no-array-index-key */
import React from 'react';
import { View } from 'react-native';
import PropTypes from 'prop-types';
import moment from 'moment';
import 'moment-range';
import { Inline } from '@styleseat/ui';
import ssPropTypes from '../../../modules/ssPropTypes';
import Cell from './Cell';
import { Stack } from '..';
import './Calendar.scss';
import { useIsMobile } from '../../../hooks';
import { getWeek } from '../../../modules/dateUtils';

const TARGET_WEEK_LENGTH = 7;
const DAYS_PER_WEEK = 7;

/**
 * Given a 2D array of days indexed by week in month and day in week, add a list of Moment objects
 * to the correct positions within the 2D array. For days outside of the target month, specify the
 * `weekIdx` directly.
 * @param {Array<Moment>} days The set of days to be appended
 * @param {boolean} empty True if the days should be marked as "empty"
 * @param {Array<{day: Moment, empty: boolean}>} weeks The list of weeks to append days to
 * @param {Number} weekIdx Specifies the index within `weeks` to add to
 */
function appendWeek(days, empty, weeks, weekIdx) {
  days.forEach(day => {
    const destWeek = weekIdx || getWeek(day, false);

    if (!weeks[destWeek]) {
      // eslint-disable-next-line no-param-reassign
      weeks[destWeek] = new Array(DAYS_PER_WEEK).fill(null);
    }
    // eslint-disable-next-line no-param-reassign
    weeks[destWeek][day.day()] = {
      day,
      empty,
    };
  });
}

/**
 * Displays a grid of calendar dates for a given month and year, with weeks starting on Sunday.
 * @param {Object} param0 Component props
 * @param {Function} [param0.onDateClicked] Called when a date on the calendar is clicked, supplied
 * with moment representing the date.
 * @param {Object} [param0.dateProps] Map of date to props for individual date cells
 * @param {moment} param0.currentDate The beginning of the month to display
 * @example
 * import DateCalendar from 'components/ui/date-picker/DateCalendar';
 *
 * const MyComponent = () => {
 *   return (
 *     <DateCalendar
 *       onDateClicked={handleDateClicked}
 *       dateProps={{25: {disabled: true}, 30: {selected: true}}}
 *       currentDate={moment().startOf('month')}
 *       month={3}
 *       year={2019}
 *     />
 *   );
 * }
 */
const DateCalendar = ({
  onDateClicked,
  dateProps,
  startDate,
}) => {
  const isMobile = useIsMobile();
  const startOfMonth = startDate
    .clone()
    .startOf('month');
  const endOfMonth = startOfMonth.clone().endOf('month');
  const days = moment
    .range(startOfMonth, endOfMonth)
    .toArray('days');

  const monthWeekStart = startOfMonth.clone().subtract(startOfMonth.day(), 'day');
  const monthWeekEnd = endOfMonth.clone().add(6 - endOfMonth.day(), 'day');
  const startEmptyCells = moment.range(monthWeekStart, startOfMonth).toArray('days');
  const endEmptyCells = moment.range(endOfMonth.clone().add(1, 'day'), monthWeekEnd).toArray('days');

  const weeks = []; // array of arrays

  appendWeek(startEmptyCells, true, weeks, 1);
  appendWeek(days, false, weeks);
  appendWeek(endEmptyCells, true, weeks, weeks.length - 1);

  if (weeks.length < TARGET_WEEK_LENGTH) {
    for (let i = weeks.length; i < TARGET_WEEK_LENGTH; i++) {
      weeks[i] = new Array(DAYS_PER_WEEK).fill(null).map(() => ({
        empty: true,
        day: moment(),
      }));
    }
  }

  return (
    <Stack
      justifyContent="space-evenly"
      alignItems="stretch"
      minWidth={isMobile ? 320 : 400}
      space={16}
    >
      {weeks.map((week, weekIdx) => (
        <Inline key={weekIdx} justifyContent="space-evenly" alignItems="center">
          {week.map((dayInfo, dayIdx) => {
            if (dayInfo) {
              const { day, empty } = dayInfo;
              return (
                <Cell
                  {...(empty ? {} : dateProps[day.date()])}
                  key={dayIdx}
                  value={day}
                  onClick={empty ? null : () => onDateClicked(day)}
                  empty={empty}
                />
              );
            }

            return <View key={dayIdx} />;
          })}
        </Inline>
      ))}
    </Stack>
  );
};

DateCalendar.propTypes = {
  /* supplied with the date that was clicked, wrapped in a moment */
  onDateClicked: PropTypes.func,
  /* given a date ordinal (1-31), returns props to apply to that date */
  dateProps: PropTypes.object,
  startDate: ssPropTypes.moment,
};

DateCalendar.defaultProps = {
  onDateClicked: () => { },
  dateProps: {},
  startDate: moment(),
};

export default DateCalendar;
